'use client';

import { lazy, Suspense, useState } from 'react';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import { Sigma } from 'lucide-react';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github.css';
import { MathCheatsheet } from './MathCheatsheet';

// SSR guard — react-md-editor uses browser APIs; React.lazy replaces next/dynamic
const MDEditor = lazy(() => import('@uiw/react-md-editor').then((m) => ({ default: m.default })));

export interface RichTextEditorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  height?: number;
}

const PREVIEW_OPTIONS = {
  remarkPlugins: [remarkMath, remarkGfm],
  rehypePlugins: [rehypeKatex, rehypeHighlight],
};

export function RichTextEditor({
  label,
  value,
  onChange,
  placeholder,
  error,
  height = 220,
}: RichTextEditorProps) {
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);

  return (
    <>
      <div>
        {label && (
          <label className="block text-sm font-semibold text-slate-900 mb-1.5">
            {label}
          </label>
        )}
        <div data-color-mode="light">
          <Suspense fallback={<div style={{ height }} className="bg-slate-50 border border-slate-200 rounded-md" />}>
            <MDEditor
              value={value}
              onChange={(v) => onChange(v ?? '')}
              height={height}
              preview="live"
              textareaProps={{ placeholder }}
              previewOptions={PREVIEW_OPTIONS}
            />
          </Suspense>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-400">
            Mendukung Markdown · Math: <code className="bg-slate-100 px-1 rounded">$x^2$</code> (inline) dan <code className="bg-slate-100 px-1 rounded">$$...$$</code> (blok) · Kode: <code className="bg-slate-100 px-1 rounded">```python</code>
          </p>
          <button
            type="button"
            onClick={() => setCheatsheetOpen(true)}
            className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium shrink-0 transition-colors"
          >
            <Sigma className="w-3.5 h-3.5" />
            Simbol Math
          </button>
        </div>
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>

      <MathCheatsheet open={cheatsheetOpen} onClose={() => setCheatsheetOpen(false)} />
    </>
  );
}
